import useWidth from '@components/Hooks/useWidth';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import ListItemIcon from '@components/v2/ui/List/ListItemIcon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Theme } from '@mui/material';
import { Ellipsis, EllipsisVertical } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useMoreActionsButton } from './hooks/useMoreActionsButton';
import { MoreActionsButtonProps } from './moreActionsButton.types';

const MENU_SX = (theme: Theme) => ({
  '& .MuiList-root': {
    padding: theme.spacing(0.5, 0.25),
    gap: theme.spacing(0.25),
  },
});

const DESTRUCTIVE_ITEM_SX = { '& .MuiListItemIcon-root': { color: 'error.main' } };

const MoreActionsButton = ({
  actions,
  trigger = { variant: 'icon' },
  testId,
}: MoreActionsButtonProps) => {
  const { translate } = useTranslation();
  const { isCustomizedTabletDown } = useWidth();
  const { anchorEl, isOpen, open, close, handleItemClick } = useMoreActionsButton();

  if (actions.length === 0) {
    return null;
  }

  const isTextTrigger = trigger.variant === 'text' && !isCustomizedTabletDown;
  const TriggerIcon = trigger.icon ?? (isTextTrigger ? Ellipsis : EllipsisVertical);
  const triggerSize = trigger.size ?? 'medium';
  const iconTriggerColor = trigger.variant === 'text' ? undefined : trigger.color;
  const iconAriaLabel =
    trigger.ariaLabel ??
    (trigger.variant === 'text' ? trigger.label : undefined) ??
    LangEnum.GLOBAL_MORE_ACTIONS;

  return (
    <>
      {isTextTrigger ? (
        <Button
          variant="text"
          color="secondary"
          size={triggerSize}
          testId={testId}
          startIcon={<TriggerIcon />}
          onClick={open}
        >
          {translate(trigger.label ?? LangEnum.GLOBAL_MORE_ACTIONS)}
        </Button>
      ) : (
        <IconButton
          icon={TriggerIcon}
          size={triggerSize}
          color={iconTriggerColor}
          testId={testId}
          ariaLabel={translate(iconAriaLabel)}
          ariaExpanded={isOpen}
          onClick={open}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={MENU_SX}
        onClose={close}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            testId={action.testId}
            sx={action.variant === 'destructive' ? DESTRUCTIVE_ITEM_SX : undefined}
            onClick={() => handleItemClick(action)}
          >
            {action.icon && (
              <ListItemIcon>
                <Icon icon={action.icon} size="md" />
              </ListItemIcon>
            )}
            <Typography color={action.variant === 'destructive' ? 'error' : 'textPrimary'}>
              {translate(action.label)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MoreActionsButton;
